import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaLink, FaCopy, FaEnvelope, FaBan, FaSyncAlt, FaShieldAlt } from 'react-icons/fa';
import CustomSpinner from '../../../Components/CommonComponents/CustomSpinner';
import ConfirmDialog from '../../../Components/CommonComponents/ConfirmDialog';
import {
  fetchAccessLinks,
  createAccessLink,
  updateAccessLink,
  revokeAccessLink,
  sendAccessLink,
  buildPortalLink,
} from '../../../Services/patientProfile';

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const EXPIRY_OPTIONS = [
  { value: 0, label: 'Never expires' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
];

// Each one is enforced by the server on the patient's link, not just hidden.
const PORTAL_SECTIONS = [
  { key: 'vitals', label: 'My vitals', detail: 'See and record assigned vitals' },
  { key: 'pictures', label: 'Pictures', detail: 'See and upload pictures' },
  { key: 'documents', label: 'Documents', detail: 'See clinic documents and upload reports' },
  { key: 'dietForm', label: 'Diet form', detail: 'Fill in the diet & nutrition questionnaire' },
  { key: 'videoConsultation', label: 'Video consultation', detail: 'See and join their video consultations' },
  { key: 'profile', label: 'My details', detail: 'See and update their own details' },
];

const DEFAULT_ALLOW = PORTAL_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: true }), {});

const toAllow = (allow) =>
  PORTAL_SECTIONS.reduce((acc, s) => ({ ...acc, [s.key]: allow?.[s.key] !== false }), {});

const hasAnySection = (allow) => PORTAL_SECTIONS.some((s) => allow[s.key]);

const SectionCheckboxes = ({ value, onChange, disabled }) => (
  <div className="space-y-1.5">
    {PORTAL_SECTIONS.map((s) => (
      <label key={s.key} className="flex items-start gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          className="mt-1"
          checked={value[s.key]}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, [s.key]: e.target.checked })}
        />
        <span>
          <span className="font-medium">{s.label}</span>
          <span className="block text-xs text-slate-500">{s.detail}</span>
        </span>
      </label>
    ))}
  </div>
);

export default function PortalLinkTab({ patientId, patient, onChanged }) {
  const [links, setLinks] = useState(null);
  const [working, setWorking] = useState(false);
  const [expiryDays, setExpiryDays] = useState(0);
  const [allow, setAllow] = useState(DEFAULT_ALLOW);
  const [editAllow, setEditAllow] = useState(DEFAULT_ALLOW);
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [pendingCreate, setPendingCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      setLinks(await fetchAccessLinks(patientId));
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not load portal links');
      setLinks([]);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const active = (links || []).find((l) => l.active);
  const activeAllow = toAllow(active?.allow);
  const sectionsChanged = !!active && PORTAL_SECTIONS.some((s) => editAllow[s.key] !== activeAllow[s.key]);

  const activeAllowKey = JSON.stringify(activeAllow);
  useEffect(() => {
    setEditAllow(toAllow(active?.allow));
  }, [active?._id, activeAllowKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    const sections = active ? editAllow : allow;
    if (!hasAnySection(sections)) return toast.error('Choose at least one section to share');
    setWorking(true);
    try {
      await createAccessLink(patientId, { expiryDays, allow: sections });
      toast.success('Portal link created');
      setPendingCreate(false);
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not create the link');
    } finally {
      setWorking(false);
    }
  };

  const saveSections = async () => {
    if (!hasAnySection(editAllow)) return toast.error('Choose at least one section to share');
    setWorking(true);
    try {
      await updateAccessLink(active._id, { allow: editAllow });
      toast.success('Portal sections updated');
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not update the link');
    } finally {
      setWorking(false);
    }
  };

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(buildPortalLink(token));
      toast.success('Link copied');
    } catch (error) {
      toast.error('Could not copy the link');
    }
  };

  const email = async (link) => {
    if (!patient?.email) return toast.error('No email address on file for this patient');
    setWorking(true);
    try {
      const res = await sendAccessLink(link._id);
      toast.success(res?.message || 'Link sent');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not send the link');
    } finally {
      setWorking(false);
    }
  };

  const revoke = async () => {
    setWorking(true);
    try {
      await revokeAccessLink(pendingRevoke._id);
      toast.success('Link revoked');
      setPendingRevoke(null);
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Could not revoke the link');
    } finally {
      setWorking(false);
    }
  };

  if (links === null) return <CustomSpinner />;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <FaShieldAlt className="mt-0.5 shrink-0 text-amber-600" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold">The link is the password</p>
          <p className="mt-0.5 text-amber-800">
            Anyone holding this link can see and add to this patient's records without logging in. Send it only to
            the patient, and revoke it if it is ever shared by mistake.
          </p>
        </div>
      </div>

      {active ? (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="inline-flex items-center gap-2 font-semibold text-slate-800">
              <FaLink className="text-primary-500" /> Active patient link
            </h3>
            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
              Active
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-700">
              {buildPortalLink(active.token)}
            </code>
            <button
              onClick={() => copy(active.token)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              <FaCopy /> Copy
            </button>
            <button
              onClick={() => email(active)}
              disabled={working || !patient?.email}
              title={patient?.email ? `Send to ${patient.email}` : 'No email address on file'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <FaEnvelope /> Email
            </button>
            <button
              onClick={() => setPendingRevoke(active)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50"
            >
              <FaBan /> Revoke
            </button>
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-4 border-t border-slate-100 pt-4 text-sm md:grid-cols-4">
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Created</dt>
              <dd className="mt-0.5 text-slate-800">{fmtDateTime(active.createdAt)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Expires</dt>
              <dd className="mt-0.5 text-slate-800">{active.expiresAt ? fmtDateTime(active.expiresAt) : 'Never'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Last opened</dt>
              <dd className="mt-0.5 text-slate-800">{fmtDateTime(active.lastAccessedAt)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-slate-500">Times opened</dt>
              <dd className="mt-0.5 text-slate-800">{active.accessCount || 0}</dd>
            </div>
          </dl>

          <div className="mt-4 border-t border-slate-100 pt-4">
            <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Sections on this link
            </span>
            <p className="mt-0.5 text-xs text-slate-500">
              Changes apply to this same link straight away — no need to send a new one.
            </p>
            <div className="mt-2">
              <SectionCheckboxes value={editAllow} onChange={setEditAllow} disabled={working} />
            </div>
            {sectionsChanged && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={saveSections}
                  disabled={working || !hasAnySection(editAllow)}
                  className="rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-white hover:bg-primary-600 disabled:opacity-60"
                >
                  {working ? 'Saving…' : 'Save sections'}
                </button>
                <button
                  onClick={() => setEditAllow(activeAllow)}
                  disabled={working}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => setPendingCreate(true)}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800"
          >
            <FaSyncAlt /> Issue a new link (revokes this one)
          </button>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-semibold text-slate-800">Give this patient a link</h3>
          <p className="mt-1 text-sm text-slate-500">
            They can open it on any phone — no account, no password — to upload documents and record their vitals.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Valid for</label>
              <select
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                value={expiryDays}
                onChange={(e) => setExpiryDays(Number(e.target.value))}
              >
                {EXPIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Sections they can see
              </span>
              <div className="mt-2">
                <SectionCheckboxes value={allow} onChange={setAllow} disabled={working} />
              </div>
            </div>
          </div>

          <button
            onClick={create}
            disabled={working || !hasAnySection(allow)}
            className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-60"
          >
            <FaLink /> {working ? 'Creating…' : 'Create patient link'}
          </button>
        </div>
      )}

      {/* Revoked links stay listed so it is clear who was given access and when. */}
      {links.filter((l) => !l.active).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700">Previous links</h3>
          <ul className="mt-3 divide-y divide-slate-100 text-sm">
            {links
              .filter((l) => !l.active)
              .map((l) => (
                <li key={l._id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-slate-600">Created {fmtDateTime(l.createdAt)}</span>
                  <span className="text-xs text-slate-500">
                    opened {l.accessCount || 0}× · revoked {fmtDateTime(l.revokedAt)}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!pendingRevoke}
        title="Revoke this link?"
        message="The patient will no longer be able to open their records with it. You can issue a new link afterwards."
        confirmLabel="Revoke"
        isProcessing={working}
        onConfirm={revoke}
        onCancel={() => setPendingRevoke(null)}
      />

      <ConfirmDialog
        isOpen={pendingCreate}
        title="Issue a new link?"
        message="The current link stops working immediately. Anyone who still has it — including the patient — will need the new one."
        confirmLabel="Issue new link"
        destructive={false}
        isProcessing={working}
        onConfirm={create}
        onCancel={() => setPendingCreate(false)}
      />
    </div>
  );
}
